import React from "react";

export const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
export const ACCOUNT_STATUS_OPTIONS = ["Active", "Inactive", "Blocked"];
export const COLORS = {
  staking: "#0f766e",
  brokerage: "#2563eb",
  earned: "#0f766e",
  generated: "#c2410c",
  direct: "#0d9488",
  downline: "#7c3aed",
  level: ["#0f766e", "#0d9488", "#14b8a6", "#5eead4", "#99f6e4"],
};

export const KYC_LABELS = {
  0: "Not submitted",
  1: "Pending",
  2: "Verified",
  3: "Rejected",
};
const KYC_CLASS = { 0: "muted", 1: "pending", 2: "ok", 3: "bad" };

export const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const formatAmount = (v) => {
  const n = asNum(v);
  if (Math.abs(n) >= 1000) return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return n.toLocaleString("en-IN", { maximumFractionDigits: 8 });
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const personName = (u) => {
  const parts = [u?.first_name, u?.last_name, u?.firstName, u?.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

export const kycLabel = (v) => KYC_LABELS[Number(v)] ?? (v == null || v === "" ? "—" : String(v));
export const kycClass = (v) => KYC_CLASS[Number(v)] || "muted";
export const accountClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "ok";
  if (s === "blocked") return "bad";
  return "muted";
};

export const formatPercent = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return `${v}%`;
  return `${n}%`;
};

export const formatCurrencyLabel = (v) => {
  const s = String(v || "").trim();
  if (!s) return "—";
  if (/^[a-fA-F0-9]{24}$/.test(s)) return "token";
  return s;
};

export const dateToFromIso = (d) => (d ? `${d}T00:00:00.000Z` : undefined);
export const dateToToIso = (d) => (d ? `${d}T23:59:59.999Z` : undefined);

export const packageOptionLabel = (pkg) => {
  const cur = pkg?.currency ?? pkg?.currency_short_name ?? pkg?.short_name ?? "";
  const days = pkg?.duration_days != null ? `${pkg.duration_days}d` : "";
  const pct = pkg?.return_percentage != null ? `${pkg.return_percentage}%` : "";
  return [cur, days, pct].filter(Boolean).join(" · ") || pkg?._id || "Package";
};

export const stakingStatusKind = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "ACTIVE") return "ok";
  if (s === "CANCELLED") return "bad";
  return "muted";
};

export const sourceLabel = (src) => {
  const s = String(src || "").toUpperCase();
  if (s.includes("TRADE") || s.includes("BROKER")) return "Trading";
  if (s.includes("PACKAGE") || s === "STAKING_PACKAGE") return "Staking bonus";
  if (s.includes("PAYOUT") || s.includes("STAKING")) return "Staking";
  return src || "—";
};

export const Chip = ({ kind, children }) => (
  <span className={`cr-chip cr-chip--${kind}`}>{children}</span>
);

export const PersonCell = ({ person, empty = "—" }) => {
  if (!person || (!person._id && !person.email && !person.uuid)) return empty;
  return (
    <div className="cr-user-mini">
      <strong>{personName(person)}</strong>
      <span>{person.email || person.uuid || "—"}</span>
    </div>
  );
};

export const DonutChart = ({ slices, size = 160, thickness = 22, centerLabel, centerSub }) => {
  const valid = (slices || []).filter((s) => asNum(s.value) > 0);
  const total = valid.reduce((sum, s) => sum + asNum(s.value), 0);
  const radius = (size - thickness) / 2;
  const circ = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="cr-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={thickness} />
        {total > 0 &&
          valid.map((s) => {
            const len = (asNum(s.value) / total) * circ;
            const el = (
              <circle
                key={s.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={`${len} ${circ - len}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += len;
            return el;
          })}
      </svg>
      <div className="cr-donut__center">
        <strong>{centerLabel}</strong>
        {centerSub ? <span>{centerSub}</span> : null}
      </div>
    </div>
  );
};

export const BarList = ({ items }) => {
  const max = Math.max(...(items || []).map((i) => asNum(i.value)), 0) || 1;
  if (!(items || []).some((i) => asNum(i.value) > 0)) {
    return <p className="cr-muted">No amounts to chart yet.</p>;
  }
  return (
    <div className="cr-bars">
      {(items || []).map((item) => (
        <div key={item.label} className="cr-bar-row">
          <span className="cr-bar-row__label" title={item.label}>
            {item.label}
          </span>
          <div className="cr-bar-row__track">
            <div
              className="cr-bar-row__fill"
              style={{
                width: `${(asNum(item.value) / max) * 100}%`,
                background: item.color || COLORS.earned,
              }}
            />
          </div>
          <span className="cr-bar-row__value">{formatAmount(item.value)}</span>
        </div>
      ))}
    </div>
  );
};

export const ChartLegend = ({ items }) => (
  <ul className="cr-legend">
    {items.map((item) => (
      <li key={item.label}>
        <span style={{ background: item.color }} />
        {item.label}
        <strong>{formatAmount(item.value)}</strong>
      </li>
    ))}
  </ul>
);

export const PaginationBar = ({ pagination, loading, onPageChange, onLimitChange }) => {
  const page = pagination.page || 1;
  const limit = pagination.limit || 20;
  const total = pagination.total ?? 0;
  const totalPages = pagination.total_pages || 1;
  const hasPrev = pagination.has_prev ?? page > 1;
  const hasNext = pagination.has_next ?? page < totalPages;
  const pageStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const pageEnd = total === 0 ? 0 : Math.min(page * limit, total);
  return (
    <div className="cr-pagination">
      {onLimitChange ? (
        <label>
          Rows
          <select
            value={limit}
            disabled={loading}
            onChange={(e) => onLimitChange(Number(e.target.value) || 20)}
          >
            {PAGE_LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <span>
        {total > 0 ? `Showing ${pageStart}–${pageEnd} of ${total}` : "No rows"}
        {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
      </span>
      <div>
        <button type="button" disabled={!hasPrev || loading} onClick={() => onPageChange(page - 1)}>
          Prev
        </button>
        <button type="button" disabled={!hasNext || loading} onClick={() => onPageChange(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
};
